import React, { Suspense } from 'react';
import {
  Await,
  Navigate,
  useFetcher,
  useLoaderData,
  useMatch,
  useNavigate,
  useBlocker,
  useBeforeUnload
} from 'react-router-dom';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '@oysterjs/ui/dialog';
import { ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { PageTitle } from '@oysterjs/ui/page';
import {
  Action,
  type AgencyDesignFieldsFragment,
  type AgencyDesignIncludedPagesFieldsFragment,
  type CreateAgencyDesignMutation,
  type GetAgencyDesignsQuery,
  Resource,
  type UpdateAgencyDesignMutation
} from '~/api/graphql';
import {
  UploadRefusedError,
  uploadAgencyBrandLogo,
  uploadAgencyDesignPage,
  type UploadedFile
} from '~/api/data/designs/mutations';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useBrandWebFonts } from '~/components/brand-web-fonts';
import { PendingChangesSaveToast } from '~/components/settings/pending-changes-save';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import {
  type VisibilitySelectorValue,
  visibilityInputFromValue,
  visibilityValueFromFragment
} from '~/components/visibility/visibility-selector.utils';
import { usePermissions } from '~/components/permissions';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import {
  BrandLogoState,
  BrandColorScheme,
  BrandState,
  brandStateFrom,
  sameBrand,
  toBrandInput,
  ELEMENTS,
  ElementStyle
} from './design-brand';
import { LogoDraftContext, TypefaceOption } from './design-fields';
import { DesignList } from './design-list';
import {
  type DesignPageState,
  type IncludedPagesState,
  IncludedPagesFields,
  PAGES_TOO_LARGE,
  pagesTooLarge,
  samePages
} from './design-pages';
import { BrandControls, TABS, Tab } from './design-panel';
import {
  DESIGN_EDIT_PATTERN,
  DESIGN_NEW_PATH,
  DESIGN_PATH,
  LEGACY_DESIGN_EDIT_PATH,
  designEditPath
} from './design-paths';
import { DocumentPreview, Region } from './design-preview';

/** A design as the editor holds it. A save carries visibility only where the editor offers it. */
type DesignState = {
  name: string;
  brand: BrandState;
  pages: IncludedPagesState;
  visibility?: VisibilitySelectorValue;
  /** When the editor opened the design, so a save made since is refused rather than replaced. */
  expectedUpdatedAt?: AgencyDesignFieldsFragment['updatedAt'];
};

/** A design as the server holds it, which always has a visibility. */
type SavedDesignState = DesignState & {
  visibility: VisibilitySelectorValue;
  updatedAt?: AgencyDesignFieldsFragment['updatedAt'];
};

const FOLLOWS_SAVES =
  'Documents that use this design follow saved changes, except versions marked final.';

/** The server holds the default design visible to everyone. */
const DEFAULT_VISIBLE = 'The default design is visible to everyone';
const DEFAULT_LOCK = { label: 'Everyone', reason: DEFAULT_VISIBLE };

const sameVisibility = (a: VisibilitySelectorValue, b: VisibilitySelectorValue) =>
  a.level === b.level &&
  [...a.userIds].sort().join() === [...b.userIds].sort().join() &&
  [...a.teamIds].sort().join() === [...b.teamIds].sort().join();

/** How the last save went: the design as the server now holds it, or why it refused. */
type SaveOutcome =
  | { saved: { id: string; design: SavedDesignState } }
  | { error: string; code?: ApplicationErrorCode };

type LocalFile = { file: File; uploaded?: UploadedFile };
type LocalLogo = LocalFile & { url: string };

const NO_PAGES: IncludedPagesState = { before: [], after: [] };

/** The key the editor for a design that has not been saved yet goes by. */
const NEW_DESIGN = 'new';

/** Records each upload on its entry, so a retry after a failed save does not send it twice. */
const uploadStaged = async (
  fileIds: string[],
  staged: Map<string, LocalFile>,
  upload: (file: File) => Promise<UploadedFile>
) => {
  await Promise.all(
    fileIds.map(async (fileId) => {
      const local = staged.get(fileId);
      if (local && !local.uploaded) local.uploaded = await upload(local.file);
    })
  );
};

const DesignEditor = ({
  design,
  isNew,
  isDefault,
  createdById,
  typefaces,
  colorSchemes,
  defaults,
  onSave,
  onSaveAsNew,
  saving,
  outcome
}: {
  design: SavedDesignState;
  isNew: boolean;
  isDefault: boolean;
  createdById: string | null;
  typefaces: TypefaceOption[];
  colorSchemes: BrandColorScheme[];
  defaults: ElementStyle[];
  onSave: (next: DesignState) => void;
  /** Creates a design from the draft, for a draft its own design can no longer take. */
  onSaveAsNew?: (next: DesignState) => void;
  saving?: boolean;
  outcome?: SaveOutcome | null;
}) => {
  useBrandWebFonts();
  const { hasPermission } = usePermissions();
  const voiceEditable = !isDefault || hasPermission(Resource.Agency, Action.Update);

  const [history, setHistory] = React.useState<BrandState[]>([design.brand]);
  const [at, setAt] = React.useState(0);
  const draft = history[at];
  const [name, setName] = React.useState(design.name);
  const [pages, setPages] = React.useState(design.pages);
  const [visibility, setVisibility] = React.useState(design.visibility);

  // As the Library's editors decide it: the creator or an admin sets an existing design's
  // visibility, and anyone entitled sets a new one's.
  const visibilityEntitled = useVisibilityEntitlement();
  const ownerMaySetVisibility = useVisibilityEditAllowed(createdById);
  const visibilityLocked = visibilityEntitled && !isNew && isDefault;
  const visibilityShown = isNew ? visibilityEntitled : ownerMaySetVisibility || visibilityLocked;

  // Compare with the mutation result while the loader catches up.
  const [baseline, setBaseline] = React.useState(design);
  const localLogos = React.useRef(new Map<string, LocalLogo>());
  const localPages = React.useRef(new Map<string, LocalFile>());
  const [uploading, setUploading] = React.useState(false);
  const allowLeave = React.useRef(false);

  React.useEffect(
    () => () => {
      for (const logo of localLogos.current.values()) URL.revokeObjectURL(logo.url);
    },
    []
  );
  const resolveLogo = (logo: BrandLogoState): BrandLogoState => {
    const uploaded = localLogos.current.get(logo.fileId)?.uploaded;
    return uploaded
      ? { ...logo, fileId: uploaded.id, url: uploaded.url, name: uploaded.name }
      : logo;
  };
  const resolvePage = (page: DesignPageState): DesignPageState => {
    const uploaded = localPages.current.get(page.fileId)?.uploaded;
    return uploaded ? { ...page, fileId: uploaded.id, name: uploaded.name } : page;
  };
  const resolvePages = (each: IncludedPagesState): IncludedPagesState => ({
    before: each.before.map(resolvePage),
    after: each.after.map(resolvePage)
  });
  const logoDrafts = {
    stage: (file: File) => {
      const id = `local-logo-${localLogos.current.size}`;
      localLogos.current.set(id, { file, url: URL.createObjectURL(file) });
      return id;
    },
    url: (mark: BrandLogoState) => localLogos.current.get(mark.fileId)?.url ?? mark.url ?? undefined
  };
  const stagePage = (file: File) => {
    const id = `local-page-${localPages.current.size}`;
    localPages.current.set(id, { file });
    return id;
  };

  const savingAsNew = React.useRef(false);
  const [nameRequired, setNameRequired] = React.useState(false);
  // A new design autofocuses an empty Name, so leaving it untouched is not yet a mistake.
  const nameEdited = React.useRef(false);
  const nameInput = React.useRef<HTMLInputElement>(null);
  const save = async (asNew = false) => {
    if (unnamed) {
      setNameRequired(true);
      nameInput.current?.focus();
      return;
    }
    savingAsNew.current = asNew;
    setUploading(true);
    setVisibleError(null);
    try {
      try {
        await uploadStaged(
          draft.logos.map((logo) => logo.fileId),
          localLogos.current,
          uploadAgencyBrandLogo
        );
      } catch (error) {
        // console.error is what reaches Sentry; the page shows only a generic message.
        // eslint-disable-next-line no-console
        console.error('Brand logo upload failed', error);
        setVisibleError(
          error instanceof UploadRefusedError ? error.message : 'Logo upload failed. Try again.'
        );
        return;
      }
      try {
        await uploadStaged(
          [...pages.before, ...pages.after].map((page) => page.fileId),
          localPages.current,
          uploadAgencyDesignPage
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Design page upload failed', error);
        setVisibleError(
          error instanceof UploadRefusedError ? error.message : 'Page upload failed. Try again.'
        );
        return;
      }
      const next = {
        name,
        brand: { ...draft, logos: draft.logos.map(resolveLogo) },
        pages: resolvePages(pages),
        visibility: visibilityShown && !visibilityLocked ? visibility : undefined
      };
      if (asNew) onSaveAsNew?.(next);
      else onSave({ ...next, expectedUpdatedAt: baseline.updatedAt });
    } finally {
      setUploading(false);
    }
  };
  React.useEffect(() => {
    if (outcome && 'saved' in outcome && savingAsNew.current) {
      // The page moves to the new design next, leaving this one as the server holds it.
      allowLeave.current = true;
      return;
    }
    if (outcome && 'saved' in outcome) {
      setBaseline(outcome.saved.design);
      setVisibility(outcome.saved.design.visibility);
      setHistory((entries) =>
        entries.map((entry) => ({ ...entry, logos: entry.logos.map(resolveLogo) }))
      );
      setPages(resolvePages);
      // The page moves to the saved design's own address next, which is no reason to ask.
      if (isNew) allowLeave.current = true;
    }
  }, [outcome]);

  const [visibleError, setVisibleError] = React.useState<string | null>(null);
  const [conflicted, setConflicted] = React.useState(false);
  React.useEffect(() => {
    setVisibleError(outcome && 'error' in outcome ? outcome.error : null);
    setConflicted(
      !!outcome && 'error' in outcome && outcome.code === ApplicationErrorCode.Conflict
    );
  }, [outcome]);
  const colorEditStart = React.useRef<number | null>(null);
  const change = (next: BrandState) => {
    const from = colorEditStart.current ?? at;
    setHistory([...history.slice(0, from + 1), next]);
    setAt(from + 1);
    setVisibleError(null);
  };

  const [tab, setTab] = React.useState(TABS[0]);
  const [previewRegion, setPreviewRegion] = React.useState<Region>(TABS[0].region);
  const selectTab = (next: Tab) => {
    setTab(next);
    setPreviewRegion(next.region);
  };

  const dirty =
    !sameBrand(draft, baseline.brand) ||
    name.trim() !== baseline.name ||
    !samePages(pages, baseline.pages) ||
    !sameVisibility(visibility, baseline.visibility);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !allowLeave.current && currentLocation.pathname !== nextLocation.pathname
  );
  useBeforeUnload(
    React.useCallback(
      (event: BeforeUnloadEvent) => {
        if (dirty && !allowLeave.current) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [dirty]
    )
  );

  const tooLarge = pagesTooLarge(pages, baseline.pages);
  const unnamed = name.trim() === '';
  const canSave = dirty && !tooLarge;

  return (
    <LogoDraftContext.Provider value={logoDrafts}>
      <PageTitle
        eyebrow="Design"
        eyebrowHref={DESIGN_PATH}
        title={isNew ? 'New design' : baseline.name}
        description={
          isNew
            ? "Once saved, choose it from a document's Design menu."
            : visibilityLocked
              ? `${FOLLOWS_SAVES} ${DEFAULT_VISIBLE}.`
              : FOLLOWS_SAVES
        }
        menu={
          visibilityShown ? (
            <VisibilitySplitButton
              value={visibility}
              onChange={setVisibility}
              locked={visibilityLocked ? DEFAULT_LOCK : undefined}
              disabled={!canSave}
              matchDisabledAppearance={!canSave}
              title={tooLarge ? PAGES_TOO_LARGE : unnamed ? NAME_REQUIRED : undefined}
              loading={saving || uploading}
              onClick={() => void save()}
            >
              Save design
            </VisibilitySplitButton>
          ) : (
            <Button
              color="sky"
              disabled={!canSave}
              title={tooLarge ? PAGES_TOO_LARGE : unnamed ? NAME_REQUIRED : undefined}
              loading={saving || uploading}
              onClick={() => void save()}
            >
              Save design
            </Button>
          )
        }
      >
        <div
          className="flex flex-col gap-6 lg:flex-row lg:items-start"
          onFocusCapture={(event) => {
            if (event.target instanceof HTMLInputElement && event.target.type === 'color')
              colorEditStart.current = at;
          }}
          onBlurCapture={(event) => {
            if (event.target instanceof HTMLInputElement && event.target.type === 'color')
              colorEditStart.current = null;
          }}
        >
          <div className="w-full shrink-0 lg:sticky lg:top-6 lg:flex lg:max-h-[calc(100vh-3rem)] lg:w-2/5 lg:max-w-[26rem] lg:flex-col">
            <BrandControls
              tab={tab}
              onTab={selectTab}
              onFocusElement={(element) =>
                setPreviewRegion(element === ELEMENTS.heading2 ? 'subheading' : tab.region)
              }
              draft={draft}
              voiceEditable={voiceEditable}
              typefaces={typefaces}
              colorSchemes={colorSchemes}
              defaults={defaults}
              error={visibleError}
              onSaveAsNew={conflicted && onSaveAsNew ? () => void save(true) : undefined}
              // The tab with the margins also holds Start and end pages, which say it themselves.
              saveHeld={tooLarge && !tab.margins ? PAGES_TOO_LARGE : null}
              canUndo={at > 0}
              canRedo={at < history.length - 1}
              onUndo={() => {
                setAt(at - 1);
                setVisibleError(null);
              }}
              onRedo={() => {
                setAt(at + 1);
                setVisibleError(null);
              }}
              onChange={change}
              head={
                <Field>
                  <Label>Name</Label>
                  <Input
                    value={name}
                    autoFocus={isNew}
                    placeholder="Design name"
                    ref={nameInput}
                    invalid={nameRequired}
                    onBlur={() => setNameRequired(unnamed && nameEdited.current)}
                    onChange={(event) => {
                      nameEdited.current = true;
                      setName(event.target.value);
                      setNameRequired(false);
                      setVisibleError(null);
                    }}
                  />
                  {nameRequired && <ErrorMessage>{NAME_REQUIRED}</ErrorMessage>}
                </Field>
              }
              pageSection={
                <IncludedPagesFields
                  pages={pages}
                  saved={baseline.pages}
                  onChange={setPages}
                  stage={stagePage}
                />
              }
            />
          </div>

          <div className="min-w-0 flex-1">
            <DocumentPreview brand={draft} typefaces={typefaces} region={previewRegion} />
          </div>
        </div>
      </PageTitle>
      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
        <DialogTitle>
          {isNew ? 'Discard this new design?' : 'Discard unsaved design changes?'}
        </DialogTitle>
        <DialogDescription>
          {isNew ? "It hasn't been saved." : 'The saved design will stay as it is.'}
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => blocker.reset?.()}>
            Keep editing
          </Button>
          <Button color="red" onClick={() => blocker.proceed?.()}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </LogoDraftContext.Provider>
  );
};

type DesignLoaderData = { data: Promise<{ designs: GetAgencyDesignsQuery }> };

const pagesFrom = (pages: AgencyDesignIncludedPagesFieldsFragment): IncludedPagesState => {
  const page = (each: AgencyDesignIncludedPagesFieldsFragment['before'][number]) => ({
    fileId: each.fileId,
    name: each.file?.name ?? 'Deleted file',
    printable: each.printable,
    size: each.file?.size ?? 0
  });
  return { before: pages.before.map(page), after: pages.after.map(page) };
};

const designStateFrom = (design: AgencyDesignFieldsFragment): SavedDesignState => ({
  updatedAt: design.updatedAt,
  name: design.name,
  brand: brandStateFrom(design.brand),
  pages: pagesFrom(design.includedPages),
  visibility: visibilityValueFromFragment(design.visibility)
});

// Time is typed as a Date but crosses the wire as the server's RFC3339 string, which keeps the
// microseconds a Date would drop; sending that string back keeps the comparison exact.
const timeInput = (time: Date | string): string =>
  typeof time === 'string' ? time : time.toISOString();

const toDesignInput = (design: DesignState) => ({
  name: design.name.trim(),
  ...toBrandInput(design.brand),
  includedPages: {
    before: design.pages.before.map((page) => page.fileId),
    after: design.pages.after.map((page) => page.fileId)
  },
  ...(design.visibility && { visibility: visibilityInputFromValue(design.visibility) }),
  ...(design.expectedUpdatedAt && { expectedUpdatedAt: timeInput(design.expectedUpdatedAt) })
});

const NAME_REQUIRED = 'Name is required.';

const SAVE_MESSAGES = {
  successTitle: 'Design saved',
  successDescription: 'Documents that use it follow the change, except versions marked final.',
  errorTitle: 'Error saving design',
  errorDescriptionPrefix: 'There was an error saving:'
};

type DesignSaveResult = ActionResult<
  Partial<CreateAgencyDesignMutation> & Partial<UpdateAgencyDesignMutation>
>;

/** What the action came back with. */
const saveOutcome = (result: DesignSaveResult): SaveOutcome | null => {
  if (!result) {
    return null;
  }
  const saved = result.data?.createAgencyDesign ?? result.data?.updateAgencyDesign;
  if (saved) {
    return { saved: { id: saved.id, design: designStateFrom(saved) } };
  }
  return {
    error:
      result.applicationError?.message ??
      result.serverError?.message ??
      result.validationErrors?.map((each) => each.message).join(' ') ??
      'Something went wrong.',
    code: result.applicationError?.code
  };
};

/** Lists the agency's designs and edits one of them, saving through the route actions. */
export const SettingsDesignRoute = () => {
  // merge() defers a single `data` promise keyed by loader name, not one promise per key.
  const loaderData = useLoaderData() as DesignLoaderData;
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const creating = !!useMatch(DESIGN_NEW_PATH);
  const editingId = useMatch(DESIGN_EDIT_PATTERN)?.params.designId ?? null;
  const legacyEdit = !!useMatch(LEGACY_DESIGN_EDIT_PATH);
  const editorKey = creating ? NEW_DESIGN : editingId;

  // One fetcher serves every editor this page opens, so its last result belongs to the editor
  // that asked for it and to no other.
  const [submittedBy, setSubmittedBy] = React.useState<string | null>(null);
  // Set while an existing design's editor saves its draft as a new design.
  const [asNew, setAsNew] = React.useState(false);
  React.useEffect(() => {
    setSubmittedBy((current) => (current === editorKey ? current : null));
  }, [editorKey]);
  const outcome = React.useMemo(
    () => saveOutcome(fetcher.data as DesignSaveResult),
    [fetcher.data]
  );
  const [toast, setToast] = React.useState(false);
  // Only a save that landed raises the toast. A refused one says why in the editor's panel,
  // which also carries the failures that never reach the server, such as an upload.
  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !outcome || !('saved' in outcome)) {
      return;
    }
    setToast(true);
    const timer = window.setTimeout(() => setToast(false), 5000);
    return () => window.clearTimeout(timer);
  }, [fetcher.state, outcome]);

  // Waits for the reload so the new design is in the list its editor is looked up in.
  React.useEffect(() => {
    if (fetcher.state !== 'idle' || (submittedBy !== NEW_DESIGN && !asNew) || !outcome) return;
    if (!('saved' in outcome)) return;
    setAsNew(false);
    setSubmittedBy(outcome.saved.id);
    navigate(designEditPath(outcome.saved.id), { replace: !asNew });
  }, [fetcher.state, submittedBy, asNew, outcome, navigate]);

  const save = (designId: string | null, next: DesignState, saveAsNew = false) => {
    setSubmittedBy(designId ?? NEW_DESIGN);
    setAsNew(saveAsNew);
    fetcher.submit(
      toDesignInput(next),
      designId && !saveAsNew
        ? { method: 'put', encType: 'application/json', action: designEditPath(designId) }
        : { method: 'post', encType: 'application/json', action: DESIGN_NEW_PATH }
    );
  };

  return (
    <Suspense
      fallback={
        editorKey ? (
          <PageTitle
            eyebrow="Design"
            eyebrowHref={DESIGN_PATH}
            title={creating ? 'New design' : 'Edit design'}
          >
            <LibraryListLoading title="Loading design…" />
          </PageTitle>
        ) : (
          <LibraryListLoading title="Loading designs…" />
        )
      }
    >
      <Await resolve={loaderData.data}>
        {({ designs: data }: { designs: GetAgencyDesignsQuery }) => (
          <>
            <DesignRouteContent
              data={data}
              creating={creating}
              editingId={editingId}
              legacyEdit={legacyEdit}
              saving={fetcher.state !== 'idle'}
              outcome={submittedBy === editorKey ? outcome : null}
              onSave={save}
            />
            <PendingChangesSaveToast saved={toast} error={null} messages={SAVE_MESSAGES} />
          </>
        )}
      </Await>
    </Suspense>
  );
};

const DesignRouteContent = ({
  data,
  creating,
  editingId,
  legacyEdit,
  saving,
  outcome,
  onSave
}: {
  data: GetAgencyDesignsQuery;
  creating: boolean;
  editingId: string | null;
  legacyEdit: boolean;
  saving: boolean;
  outcome: SaveOutcome | null;
  onSave: (designId: string | null, next: DesignState, asNew?: boolean) => void;
}) => {
  const { hasPermission } = usePermissions();
  const defaultVisibility = useDefaultVisibilityValue();
  if (!data.agency) {
    throw new Error('Agency not found');
  }
  const designs = data.agency.designs;
  const editorProps = {
    typefaces: data.brandTypefaces,
    colorSchemes: data.brandColorSchemes,
    defaults: data.brandElementDefaults ?? [],
    saving,
    outcome
  };

  if (legacyEdit) {
    const current = designs.find((design) => design.isDefault);
    return <Navigate replace to={current ? designEditPath(current.id) : DESIGN_PATH} />;
  }

  if (creating) {
    if (!hasPermission(Resource.HtmlDocumentTemplate, Action.Create)) {
      return <Navigate replace to={DESIGN_PATH} />;
    }
    return (
      <DesignEditor
        key={NEW_DESIGN}
        isNew
        isDefault={false}
        createdById={null}
        // A new design starts from the default design's brand, or the built-in one without it.
        design={{
          name: '',
          brand: brandStateFrom(data.agency.brand),
          pages: NO_PAGES,
          visibility: defaultVisibility
        }}
        onSave={(next) => onSave(null, next)}
        {...editorProps}
      />
    );
  }

  if (editingId) {
    const design = designs.find((each) => each.id === editingId);
    if (!design || !hasPermission(Resource.HtmlDocumentTemplate, Action.Update)) {
      return <Navigate replace to={DESIGN_PATH} />;
    }
    return (
      <DesignEditor
        key={design.id}
        isNew={false}
        isDefault={design.isDefault}
        createdById={design.createdBy?.id ?? null}
        design={designStateFrom(design)}
        onSave={(next) => onSave(design.id, next)}
        onSaveAsNew={(next) => onSave(design.id, next, true)}
        {...editorProps}
      />
    );
  }

  return <DesignList designs={designs} defaultDesignId={data.agency.defaultDesign?.id ?? null} />;
};
